// studio/StudioPage.jsx — AYA Creator Studio : environnement complet (maquette = source de vérité).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { ST, btnP, chip, themeVars, KEYFRAMES, STATUS_META } from "@/internal/studio/theme";
import { buildArtifacts, effectivePhase, setLocalPhase, getCredits, spendCredits, CRED_MAX } from "@/internal/studio/simEngine";
import IntentPanel from "@/internal/studio/IntentPanel";
import CodePanel from "@/internal/studio/CodePanel";
import { DevicePreview, MediaStudioPanel } from "@/internal/studio/PreviewPanel";
import BottomPanel from "@/internal/studio/BottomPanel";
import { OverviewView, ProjectsView, AgentsView, DeploymentsView, LibraryView, EnvironmentsView, MonitoringView, SettingsView } from "@/internal/studio/SideViews";
import { LayoutDashboard, Code2, FolderKanban, Bot, Clapperboard, Rocket, Activity, Search, Bell, MessageSquare, Boxes, Server, ShieldCheck, ScrollText, Settings, Eye, Sparkles, ChevronDown, Sun, Moon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });

const TOPNAV = [
  ["overview", "Dashboard", LayoutDashboard], ["studio", "Creator Studio", Code2], ["projects", "Projets", FolderKanban],
  ["agents", "Agents IA", Bot], ["media", "Media Studio", Clapperboard], ["deployments", "Déploiements", Rocket], ["monitoring", "Monitoring", Activity],
];
const SIDENAV = [
  ["overview", "Vue d'ensemble", Eye], ["studio", "Creator Studio", Code2], ["projects", "Projets", FolderKanban],
  ["agents", "Agents IA", Bot], ["media", "Media Studio", Clapperboard, "Nouveau"], ["library", "Bibliothèque UI", Boxes],
  ["components", "Base de composants", Boxes], ["deployments", "Déploiements", Rocket], ["environments", "Environnements", Server],
  ["sentinel", "Aya Sentinel", ShieldCheck], ["logs", "Logs & Audit", ScrollText], ["settings", "Paramètres", Settings],
];
const ENVS = ["Développement", "Staging", "Production"];
const VIEW_TITLES = { overview: "Vue d'ensemble", projects: "Projets", agents: "Agents IA", media: "Media Studio", library: "Bibliothèque UI", components: "Base de composants", deployments: "Déploiements", environments: "Environnements", monitoring: "Monitoring", settings: "Paramètres" };

export default function StudioPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [view, setView] = useState("studio");
  const [builds, setBuilds] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [coding, setCoding] = useState(false);
  const [compare, setCompare] = useState(false);
  const [env, setEnv] = useState("Développement");
  const [credits, setCredits] = useState(getCredits());
  const [theme, setTheme] = useState(localStorage.getItem("aya_studio_theme") || "dark");
  const [dataMode, setDataMode] = useState("simulation");
  const [, force] = useState(0);
  const timer = useRef(null);

  const toggleTheme = () => { const t = theme === "dark" ? "light" : "dark"; setTheme(t); localStorage.setItem("aya_studio_theme", t); };
  const toggleDataMode = async () => {
    const mode = dataMode === "production" ? "simulation" : "production";
    const r = await fetch(`${API}/studio/mode`, { method: "POST", headers: H(), body: JSON.stringify({ mode }) });
    const d = await r.json();
    if (!d.ok) { toast.error(`⛔ Refusé par la policy ${d.policy || "JSONFIRST"}`); return; }
    setDataMode(d.mode);
    toast.success(d.mode === "production" ? "Mode PRODUCTION — mocks désactivés dans tous les studios." : "Mode SIMULATION réactivé.");
  };
  useEffect(() => {
    fetch(`${API}/studio/mode`, { headers: H() }).then((r) => r.json()).then((d) => d.ok && setDataMode(d.mode)).catch(() => {});
  }, []);

  useEffect(() => { if (!isAuthenticated) navigate("/login?next=/internal/creator"); }, [isAuthenticated, navigate]);
  const load = useCallback(() => {
    fetch(`${API}/creator/builds`, { headers: H() }).then((r) => r.json()).then((d) => {
      if (d.ok) { setBuilds(d.builds); setActiveId((cur) => cur || d.builds[0]?.build_id || null); }
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(timer.current), []);

  const build = builds.find((b) => b.build_id === activeId) || null;
  const phase = coding && build ? "coding" : effectivePhase(build);
  const artifacts = useMemo(() => buildArtifacts(build), [build]);

  const submitIntent = async (intent) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/creator/plan`, { method: "POST", headers: H(), body: JSON.stringify({ intent }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.message || d.error);
      setCredits(spendCredits(150));
      toast.success(`Plan généré : ${d.build.plan.title}`);
      setActiveId(d.build.build_id); setView("studio"); load();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const generateCode = () => {
    if (!build || phase !== "plan" || timer.current) return;
    setCoding(true); setProgress(0); setCredits(spendCredits(300));
    timer.current = setInterval(() => setProgress((p) => {
      if (p >= 100) {
        clearInterval(timer.current); timer.current = null;
        setLocalPhase(build.build_id, "coded"); setCoding(false);
        toast.success("Code généré — tests QA réussis. Vérifiez le diff puis acceptez.");
        return 100;
      }
      return p + 2;
    }), 160);
  };

  const accept = () => {
    if (!build || phase !== "coded") return;
    setLocalPhase(build.build_id, "accepted"); force((x) => x + 1);
    toast.success("Modifications acceptées — prêt à déployer.");
  };

  const deploy = async () => {
    if (!build) return;
    if (phase !== "accepted") { toast.info(phase === "plan" ? "Générez d'abord le code." : phase === "coded" ? "Acceptez d'abord les modifications (diff)." : phase === "validation" ? "Déjà en validation Founder." : "Build déjà traité."); return; }
    const r = await fetch(`${API}/creator/builds/${build.build_id}/validate`, { method: "POST", headers: H() });
    const d = await r.json();
    if (!d.ok) { toast.error(d.error || "Erreur"); return; }
    if (d.status === "denied") toast.error(`⛔ Déploiement refusé par la policy ${d.policy || "JSONFIRST"}`);
    else toast.success(`🔐 Soumis à validation Founder (${d.policy || "JSONFIRST"}) — environnement ${env}`);
    load();
  };

  const openBuild = (id) => { setActiveId(id); setView("studio"); setCompare(false); };
  const meta = phase ? STATUS_META[phase] : null;
  const navBtn = (on) => ({ display: "flex", alignItems: "center", gap: 7, padding: "7px 13px", borderRadius: 999, border: on ? "1px solid rgba(46,226,123,0.45)" : "1px solid transparent", background: on ? "rgba(46,226,123,0.12)" : "transparent", color: on ? ST.green : ST.muted, fontSize: 12, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" });
  const sideBtn = (on) => ({ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "8px 12px", borderRadius: 10, border: "none", background: on ? "rgba(46,226,123,0.12)" : "transparent", color: on ? ST.green : ST.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", textAlign: "left", outline: on ? "1px solid rgba(46,226,123,0.35)" : "none" });

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: ST.bg, color: ST.text, fontFamily: "'Space Grotesk','Inter',sans-serif", overflow: "hidden", ...themeVars(theme) }} data-testid="studio-page">
      <style>{KEYFRAMES}</style>

      <header style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 16px", height: 54, borderBottom: `1px solid ${ST.border}`, background: ST.headbg, flexShrink: 0 }}>
        <button onClick={() => navigate("/internal")} data-testid="studio-logo" style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#1DB954,#0E7A38)", display: "grid", placeItems: "center" }}><Sparkles size={15} color="#fff" /></div>
          <span style={{ fontSize: 19, fontWeight: 900, color: ST.text, letterSpacing: 1 }}>AYA</span>
        </button>
        <div style={{ display: "flex", gap: 2, marginLeft: 12, overflowX: "auto", flex: 1 }}>
          {TOPNAV.map(([k, l, I]) => <button key={k} data-testid={`topnav-${k}`} onClick={() => setView(k)} style={navBtn(view === k)}><I size={13} />{l}</button>)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, background: ST.soft, border: `1px solid ${ST.border2}`, borderRadius: 999, padding: "6px 13px", width: 180 }}>
          <Search size={13} color={ST.muted} /><input data-testid="studio-search" placeholder="Rechercher…" style={{ background: "none", border: "none", outline: "none", color: ST.text, fontSize: 11.5, width: "100%", fontFamily: "inherit" }} />
        </div>
        <button data-testid="studio-theme-toggle" onClick={toggleTheme} style={{ background: "none", border: "none", cursor: "pointer" }}>{theme === "dark" ? <Sun size={16} color={ST.muted} /> : <Moon size={16} color={ST.muted} />}</button>
        <button style={{ position: "relative", background: "none", border: "none", cursor: "pointer" }}><MessageSquare size={16} color={ST.muted} /><span style={{ position: "absolute", top: -4, right: -5, width: 13, height: 13, borderRadius: "50%", background: ST.red, fontSize: 8, fontWeight: 900, color: "#fff", display: "grid", placeItems: "center" }}>3</span></button>
        <button style={{ background: "none", border: "none", cursor: "pointer" }}><Bell size={16} color={ST.muted} /></button>
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg,#334155,#64748B)", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 900, color: "#fff" }}>{(user?.display_name || "C")[0].toUpperCase()}</div>
          <div><div style={{ fontSize: 11.5, fontWeight: 800 }}>{user?.display_name || "Cédric D."}</div><div style={{ fontSize: 9.5, color: ST.muted }}>CTO · Founder</div></div>
        </div>
      </header>

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <aside style={{ width: 205, borderRight: `1px solid ${ST.border}`, display: "flex", flexDirection: "column", padding: "12px 10px", flexShrink: 0, overflowY: "auto" }}>
          <div style={{ padding: "0 6px 12px" }}>
            <div style={{ fontSize: 13.5, fontWeight: 900 }}>CTO Workspace</div>
            <div style={{ fontSize: 9.5, color: ST.muted, fontWeight: 700 }}>Construire · Contrôler · Déployer</div>
          </div>
          <nav style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
            {SIDENAV.map(([k, l, I, badge]) => (
              <button key={k} data-testid={`sidenav-${k}`} style={sideBtn(view === k)}
                onClick={() => { if (k === "sentinel") navigate("/internal/security"); else if (k === "logs") navigate("/internal/logs"); else setView(k === "components" ? "library" : k); }}>
                <I size={14} /><span style={{ flex: 1 }}>{l}</span>{badge && <span style={chip(ST.green)}>{badge}</span>}
              </button>
            ))}
          </nav>
          <div style={{ background: ST.card, border: `1px solid ${ST.border}`, borderRadius: 12, padding: "10px 12px", marginTop: 10 }} data-testid="studio-credits">
            <div style={{ fontSize: 10.5, fontWeight: 900, color: ST.gold }}>⚡ AYA Credits</div>
            <div style={{ height: 5, borderRadius: 999, background: "rgba(139,163,148,0.2)", margin: "7px 0 5px" }}><div style={{ width: `${Math.min(100, (credits / CRED_MAX) * 100)}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg,#3BE07A,#E8B84B)" }} /></div>
            <div style={{ fontSize: 10, color: ST.muted, fontWeight: 700 }}>{credits.toLocaleString("fr-FR")} / {CRED_MAX.toLocaleString("fr-FR")}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: ST.card, border: `1px solid ${ST.border}`, borderRadius: 12, padding: "9px 11px", marginTop: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#1DB954,#0E7A38)", display: "grid", placeItems: "center" }}><Sparkles size={12} color="#fff" /></div>
            <div><div style={{ fontSize: 11, fontWeight: 800 }}>AYA</div><div style={{ fontSize: 9, color: ST.green, fontWeight: 700 }}>● Votre assistant CTO — En ligne</div></div>
          </div>
        </aside>

        <main style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, padding: "12px 14px", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0, flexWrap: "wrap" }}>
            <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#1DB954,#0E7A38)", display: "grid", placeItems: "center" }}><Sparkles size={18} color="#fff" /></div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <h1 style={{ margin: 0, fontSize: 19, fontWeight: 900 }}>{view === "studio" ? "AYA Creator Studio" : VIEW_TITLES[view] || "AYA Studio"}</h1>
                {view === "studio" && meta && <span data-testid="studio-phase-badge" style={chip(meta.c)}>{meta.l}</span>}
                <span data-testid="studio-datamode-badge" style={chip(dataMode === "production" ? ST.red : ST.gold)}>{dataMode === "production" ? "PRODUCTION · données réelles" : "SIMULATION"}</span>
              </div>
              <div style={{ fontSize: 11, color: ST.muted }}>{view === "studio" ? "De l'intention au code, de l'image à la vidéo, tout dans un seul environnement." : "Espace privé et professionnel AYA — simulation complète du pipeline Emergent."}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, background: ST.soft, border: `1px solid ${ST.border2}`, borderRadius: 10, padding: "6px 11px" }}>
                <span style={{ fontSize: 9.5, color: ST.muted, fontWeight: 800 }}>Environnement</span>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: env === "Production" ? ST.red : env === "Staging" ? ST.gold : ST.green }} />
                <select data-testid="studio-env-select" value={env} onChange={(e) => setEnv(e.target.value)} style={{ background: "none", border: "none", color: ST.text, fontSize: 11.5, fontWeight: 800, outline: "none", cursor: "pointer" }}>
                  {ENVS.map((e2) => <option key={e2} style={{ background: ST.panel }}>{e2}</option>)}
                </select>
                <ChevronDown size={12} color={ST.muted} />
              </div>
              {["Staging", "Production"].map((e2) => (
                <button key={e2} data-testid={`studio-env-${e2.toLowerCase()}`} onClick={() => setEnv(e2)} style={{ padding: "7px 13px", borderRadius: 10, border: `1px solid ${env === e2 ? "rgba(46,226,123,0.45)" : ST.border2}`, background: env === e2 ? "rgba(46,226,123,0.1)" : "transparent", color: env === e2 ? ST.green : ST.muted, fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>{e2}</button>
              ))}
              <button data-testid="studio-deploy" onClick={deploy} style={{ ...btnP, padding: "9px 20px" }}><Rocket size={13} /> Déployer</button>
            </div>
          </div>

          {view === "studio" ? (
            <>
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(330px,1.05fr) minmax(300px,1.25fr) minmax(265px,0.85fr)", gap: 10, minHeight: 0 }}>
                <IntentPanel build={build} builds={builds} phase={phase} progress={progress} busy={busy} onSubmitIntent={submitIntent} onGenerateCode={generateCode} onOpenBuild={openBuild} />
                <CodePanel artifacts={artifacts} phase={phase} progress={progress} compare={compare} setCompare={setCompare} />
                <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 0 }}>
                  <DevicePreview build={build} phase={phase} />
                  <MediaStudioPanel build={build} prodMode={dataMode === "production"} />
                </div>
              </div>
              <div style={{ height: 212, flexShrink: 0 }}>
                <BottomPanel artifacts={artifacts} phase={phase} progress={progress} onAccept={accept} onViewDiff={() => setCompare(true)} />
              </div>
            </>
          ) : (
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              {view === "overview" && <OverviewView builds={builds} onOpen={openBuild} />}
              {view === "projects" && <ProjectsView builds={builds} onOpen={openBuild} />}
              {view === "agents" && <AgentsView />}
              {view === "media" && <div style={{ maxWidth: 560 }}><MediaStudioPanel build={build} full prodMode={dataMode === "production"} /></div>}
              {view === "library" && <LibraryView />}
              {view === "deployments" && <DeploymentsView builds={builds} onOpen={openBuild} />}
              {view === "environments" && <EnvironmentsView />}
              {view === "monitoring" && <MonitoringView />}
              {view === "settings" && <SettingsView dataMode={dataMode} onToggleMode={toggleDataMode} />}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
